import { effect } from 'kerfjs';

import { me } from '../data/state.js';

export const ACTING_READ_ONLY_REASON = 'Read-only while viewing as a member';

/** Actions that only navigate, inspect, filter, download, or leave the acting session.
 * Unknown actions are treated as writes until classified. */
const readActions = new Set([
  'ai-spend-view',
  'attachment-errors-open',
  'back-to-list',
  'chat-history',
  'chat-select',
  'chat-sort-direction',
  'chat-sort-field',
  'clarify-cancel',
  'currency-edit-cancel',
  'currency-filter-clear',
  'currency-picker-cancel',
  'currency-picker-open',
  'decision-dialog-cancel',
  'demand-open-nav',
  'demand-search',
  'demand-site',
  'demand-status',
  'demands-load-more',
  'document-download',
  'document-preview',
  'document-preview-close',
  'document-preview-shift',
  'document-preview-zoom',
  'document-select',
  'documents-clear-selection',
  'documents-copy-reference',
  'documents-download',
  'editor-seg',
  'evidence-benchmark-close',
  'evidence-close',
  'evidence-view',
  'export-savings',
  'gallery-copy-reference',
  'go-library',
  'go-library-org',
  'go-library-project',
  'go-savings',
  'go-settings',
  'go-settings-org',
  'go-settings-personal',
  'go-settings-project',
  'go-vendors',
  'lib-fact-cancel',
  'lib-tab',
  'library-doc-history',
  'library-doc-open-selected',
  'library-doc-select',
  'line-copy-reference',
  'line-editor-cancel',
  'line-evidence-open',
  'line-open',
  'member-dialog-cancel',
  'notes-review',
  'open-demand-nav',
  'open-questions',
  'open-thread-nav',
  'order-cancel',
  'pricing-prompt-dismiss',
  'questions-dialog-cancel',
  'questions-dialog-close',
  'quote-dialog-cancel',
  'quote-view',
  'receipt-cancel',
  'reopen-update-cancel',
  'retry-ai-usage',
  'retry-quotes',
  'savings-breakdown-view',
  'savings-period-nav',
  'search-clear',
  'search-expand',
  'search-help',
  'search-help-insert',
  'search-token-edit',
  'search-token-remove',
  'seg-view',
  'settings-area',
  'settings-page',
  'settings-project',
  'share-export',
  'signout',
  'source-row-find',
  'source-row-picker-close',
  'stop-acting',
  'summary-pane',
  'toggle-cancelled',
  'toggle-closed-chats',
  'toggle-drafts',
  'toggle-inspector',
  'toggle-sidebar',
  'unsaved-discard',
  'unsaved-keep',
  'vendor-cancel',
  'vendor-detail-section',
  'vendors-back',
]);

export function actingReadOnly(): boolean {
  return me.value?.actingAs != null;
}

export function actionWrites(action: string): boolean {
  return !readActions.has(action);
}

function editableSearch(el: Element): boolean {
  if (el.closest('.search-field')) return true;
  if (
    el.matches(
      '[type="search"], [data-action="demand-search"], [data-action="demand-status"], [data-action="demand-site"]',
    )
  )
    return true;
  return el.matches(
    '[name="search"], [name="filter"], [name="currency-filter"], [name="source-row-query"], [name="savings-site"], [name="analysis-version"]',
  );
}

function writeControl(el: Element): boolean {
  if (el.matches('input, textarea, select, wa-select, kui-select, [contenteditable="true"]')) {
    if (editableSearch(el) || el.matches('[type="hidden"]')) return false;
    return true;
  }
  const action = el.getAttribute('data-action');
  if (action === null || !actionWrites(action)) return false;
  return el.matches('button, a, [role="button"], [role="menuitem"], [tabindex]');
}

/** Lock controls in the mounted shell, including controls added by later renders. */
export function installActingReadOnly(): void {
  // Workbench can relocate toolbar controls across responsive roots. Observe
  // the page so the relocated node and popover surfaces remain covered.
  const scope = document.body;
  const owned = new WeakSet<Element>();
  const previousTitles = new WeakMap<Element, string | null>();
  const selector = 'input, textarea, select, wa-select, kui-select, [contenteditable="true"], [data-action]';
  const sync = (el: Element): void => {
    const writable = writeControl(el);
    // Kerf Select is a custom host whose button owns actual focus and activation.
    const controls = el.matches('kui-select')
      ? [el, el.querySelector('.kui-select__trigger')].filter((node): node is Element => node !== null)
      : [el];
    for (const control of controls) {
      if (actingReadOnly() && writable) {
        // Kerf can reuse a control and clear attributes while patching its
        // view. Keep our lock on that same node through the next patch.
        if (owned.has(control)) {
          if (!control.hasAttribute('disabled')) control.setAttribute('disabled', '');
          if (control.getAttribute('aria-disabled') !== 'true') control.setAttribute('aria-disabled', 'true');
          if (control.getAttribute('title') !== ACTING_READ_ONLY_REASON)
            control.setAttribute('title', ACTING_READ_ONLY_REASON);
          continue;
        }
        if (control.hasAttribute('disabled')) continue;
        previousTitles.set(control, control.getAttribute('title'));
        control.setAttribute('disabled', '');
        control.setAttribute('aria-disabled', 'true');
        control.setAttribute('title', ACTING_READ_ONLY_REASON);
        owned.add(control);
      } else if (owned.has(control)) {
        control.removeAttribute('disabled');
        control.removeAttribute('aria-disabled');
        const title = previousTitles.get(control);
        if (title === null || title === undefined) control.removeAttribute('title');
        else control.setAttribute('title', title);
        owned.delete(control);
      }
    }
  };
  const scan = (node: Element): void => {
    if (node.matches(selector)) sync(node);
    node.querySelectorAll(selector).forEach(sync);
  };
  const observer = new MutationObserver((records) => {
    if (!actingReadOnly()) return;
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        scan(node);
        const select = node.parentElement?.closest('kui-select');
        if (select !== null && select !== undefined) sync(select);
      }
      if (record.type === 'attributes' && record.target instanceof Element) {
        const target = record.target;
        sync(target.closest('kui-select') ?? target);
      }
    }
  });
  observer.observe(scope, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-action', 'name', 'type', 'contenteditable', 'disabled', 'aria-disabled', 'title'],
  });
  effect(() => {
    const active = me.value?.actingAs != null;
    // Kerf may commit the corresponding render in the same turn. The observer
    // handles replaced controls; this scan restores controls retained in place.
    queueMicrotask(() => {
      if ((me.value?.actingAs != null) === active) scan(scope);
    });
  });
  window.addEventListener('resize', () => {
    if (!actingReadOnly()) return;
    requestAnimationFrame(() => {
      scan(scope);
      requestAnimationFrame(() => {
        scan(scope);
      });
    });
  });
  for (const type of ['click', 'keydown'] as const)
    scope.addEventListener(
      type,
      (event) => {
        if (!actingReadOnly()) return;
        if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') return;
        const target = event.target instanceof Element ? event.target.closest('[data-action]') : null;
        if (target === null || !actionWrites(target.getAttribute('data-action') ?? '')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      { capture: true },
    );
  for (const type of ['change', 'input'] as const)
    scope.addEventListener(
      type,
      (event) => {
        if (!actingReadOnly() || !(event.target instanceof Element) || !writeControl(event.target)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      { capture: true },
    );
  scope.addEventListener(
    'submit',
    (event) => {
      if (!actingReadOnly()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    { capture: true },
  );
}
